import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LatLng } from 'leaflet';
import { Popover, PopoverContent, PopoverTrigger, SearchInput } from '@ds';
import { Button } from '@heroui/react';
import { DataTable } from '@ui';
import { AircraftMarkerListItem, AircraftStatusEnum } from '@lp/features/RoutePlanner/types';
import { useAircraftList } from '@lp/features/RoutePlanner/hooks/useAircraftList';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { checkAircraftTimeRangeAvailability } from '@lp/features/RoutePlanner/utils';
import { useRouteLegs } from '@lp/features/RoutePlanner/hooks/useRouteLegs';
import {
  AIRCRAFT_TABLE_COLUMNS,
  getCrewResources,
  getRenderAircraftTableCell,
  WarningTypeEnum,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { BaseTmdxAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { CaseResourceType } from '@/api/apis/case';
import { useWatchFields } from '@/hooks/useWatchFields';
import { SelectedAircraftInfo } from './SelectedAircraftInfo';

export const AircraftSelect = () => {
  const { caseId } = useCaseDetailsWithReviews();
  const { isMainDataEdit, addWarningMessage, removeWarningMessage } = useLegFormContext();
  const routeLegs = useRouteLegs();
  const { Aircraft: aircraftsResources, Crew: crewResources } = useRouteResources();
  const [isWarningState, setIsWarningState] = useState(false);
  const { control, setValue, getValues } = useFormContext<BaseTmdxAviaLegSchemaValues>();
  const { resources, departureLocation, selectedAircraftId, departureDateTime, arrivalDateTime } =
    useWatchFields<BaseTmdxAviaLegSchemaValues>([
      'selectedAircraftId',
      'departureDateTime',
      'arrivalDateTime',
      'departureLocation',
      'resources',
    ] as const);
  const [isOpen, setOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<AircraftMarkerListItem | null>(null);

  const departureLocationLatLng = useMemo(() => {
    if (departureLocation.latitude && departureLocation.longitude) {
      return new LatLng(departureLocation.latitude, departureLocation.longitude);
    }

    return null;
  }, [departureLocation.latitude, departureLocation.longitude]);

  const { list, onAircraftSearch, searchValue, isLoading } = useAircraftList({
    caseId,
    departureLocation: departureLocationLatLng,
    startTime: departureDateTime,
    endTime: arrivalDateTime,
  });

  const renderTableCell = useMemo(() => {
    return getRenderAircraftTableCell(selectedResource);
  }, [selectedResource]);

  useEffect(() => {
    if (list.length && selectedAircraftId) {
      const currentAircraft = list.find((a) => selectedAircraftId === a.id);

      if (currentAircraft) {
        setSelectedResource(currentAircraft);
      }
    }
  }, [selectedAircraftId, list]);

  const checkAircraftStatus = useCallback(
    (currentAircraft: AircraftMarkerListItem | null) => {
      removeWarningMessage([
        WarningTypeEnum.AircraftUnavailable,
        WarningTypeEnum.AircraftOnRest,
        WarningTypeEnum.AircraftOnDutyRange,
      ]);

      switch (currentAircraft?.status) {
        case AircraftStatusEnum.unavailable:
          addWarningMessage(WarningTypeEnum.AircraftUnavailable);
          setIsWarningState(true);
          break;
        case AircraftStatusEnum.onRest:
          addWarningMessage(WarningTypeEnum.AircraftOnRest);
          setIsWarningState(true);
          break;
        case AircraftStatusEnum.onDuty:
          const isAvailableForTimeRange = checkAircraftTimeRangeAvailability(
            currentAircraft,
            departureDateTime,
            arrivalDateTime,
          );

          if (!isAvailableForTimeRange) {
            addWarningMessage(WarningTypeEnum.AircraftOnDutyRange);
          }
          setIsWarningState(!isAvailableForTimeRange);

          break;
        default:
          setIsWarningState(false);
      }
    },
    [departureDateTime, arrivalDateTime, addWarningMessage, removeWarningMessage],
  );

  const onSelectChange = useCallback(
    (e: MouseEvent<HTMLTableRowElement>, currentAircraft: AircraftMarkerListItem) => {
      e.stopPropagation();

      if (currentAircraft && ![AircraftStatusEnum.unavailable].includes(currentAircraft.status)) {
        const currentValues = getValues().resources;
        const resourcesWithoutAircraftCrew = currentValues.filter(
          (r) => ![CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType),
        );
        const crewList = getCrewResources(currentAircraft, routeLegs, aircraftsResources);
        const aircraftCrewResources = [
          { resourceId: currentAircraft.id, resourceType: CaseResourceType.Aircraft },
          ...crewList,
        ];
        const newResources = [...resourcesWithoutAircraftCrew, ...aircraftCrewResources];

        setValue('selectedAircraftId', currentAircraft.id);
        setValue('resources', newResources);

        setOpen(false);
      }
    },
    [getValues, routeLegs, aircraftsResources, setValue],
  );

  useEffect(() => {
    checkAircraftStatus(selectedResource);
  }, [checkAircraftStatus, selectedResource]);

  const disabledAircrafts = useMemo(() => {
    return list.filter((a) => [AircraftStatusEnum.unavailable].includes(a.status)).map((a) => a.id);
  }, [list]);

  const onOpenChange = (openState: boolean) => {
    setOpen(openState);
  };

  return (
    <Controller
      name="selectedAircraftId"
      control={control}
      render={({ field: { value }, fieldState: { invalid } }) => {
        const borderWarningState = isWarningState ? 'border-warning-40' : 'border-base-55';
        const borderColor = invalid ? 'border-danger' : borderWarningState;

        return (
          <Popover id="AircraftSelect" isOpen={isOpen} size="lg" onOpenChange={onOpenChange}>
            <PopoverTrigger>
              <Button
                isDisabled={!isMainDataEdit || isLoading}
                isLoading={isLoading}
                className={`h-12 bg-base-55 aria-expanded:scale-100 border-1 ${borderColor}`}
              >
                <SelectedAircraftInfo
                  aircraft={selectedResource}
                  legResources={resources}
                  crewResources={crewResources}
                  isOpen={isOpen}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="py-2 px-1 w-[900px]">
                <SearchInput
                  className="mb-6"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => onAircraftSearch(e.target.value)}
                />
                <DataTable
                  baseClassName="max-h-60 overflow-scroll"
                  type="infinite"
                  label="Aircrafts"
                  columns={AIRCRAFT_TABLE_COLUMNS}
                  disabledKeys={disabledAircrafts}
                  selectedKeys={value ? [value] : []}
                  hidePadding
                  renderCell={renderTableCell}
                  data={list}
                  hideHeader
                  isStriped={false}
                  selectionMode="single"
                  isLoading={isLoading}
                  isFetching={isLoading && !list.length}
                  onRowClick={onSelectChange}
                />
              </div>
            </PopoverContent>
          </Popover>
        );
      }}
    />
  );
};
